import { useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  InShellPanel,
  InShellPanelDescription,
  InShellPanelHeader,
  InShellPanelTitle,
  Input,
} from '@staff-app/ui';
import { Download } from 'lucide-react';
import {
  INTL_ALL_STAGE_LABELS,
  INTL_DEVICE_LABELS,
  INTL_ROUTE_LABELS,
  formatMinor,
  isMcfServable,
  type IntlAllRequest,
} from '@staff-app/shared-types';
import { CopyButton } from '@/components/CopyButton';
import { countryName } from '@/features/intl-requests/hooks/useIntlRequests';
import { IntlInvoiceStatusBadge } from '@/features/intl-requests/components/IntlInvoiceStatusBadge';
import { AddressDialog } from './AddressDialog';
import { AirBillingDialog, type AirBillingMode } from './AirBillingDialog';
import { PricingCard } from './PricingCard';
import { RouteDialog } from './RouteDialog';
import { FulfilApproval } from './FulfilApproval';
import { InvoiceSection } from './InvoiceSection';
import { ShipmentsPanel } from './ShipmentsPanel';
import { downloadAirQuotePdf } from '../quote-pdf';
import { linesSummary, routingFrozenReason } from '../route-grid';
import {
  useAddIntlAllRequestNote,
  useCanFileAir,
  useCanFulfilAir,
  useCancelIntlAllQuote,
  useCreateIntlAllQuote,
  useIntlAllEligibleModels,
  useIntlAllInventory,
  useIntlAllMcfStock,
  useIntlAllRequestNotes,
  useIntlAllRequestPricing,
  useIsRootAir,
  useRefreshIntlAllInvoice,
  useSetIntlAllFreightOverride,
  useUnrouteIntlAllRequest,
} from '../hooks/useIntlAllRequests';

function fmt(d?: string | null): string {
  if (!d) return '—';
  const parsed = new Date(d);
  return isNaN(+parsed) ? '—' : parsed.toLocaleString();
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section className="border-t pt-4">
      <div className="mb-3 flex min-h-6 items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, wide }: { label: string; value?: ReactNode; wide?: boolean }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={`mt-0.5 whitespace-pre-wrap break-words text-sm ${empty ? 'text-muted-foreground' : ''}`}>
        {empty ? '—' : value}
      </dd>
    </div>
  );
}

/** A quote that still binds a price: issued, not cancelled, not expired (the server retires an expired one). */
export function hasLiveQuote(r: IntlAllRequest): boolean {
  return Boolean(r.stripeQuoteId) && r.quoteStatus !== 'canceled' && !r.quoteExpired;
}

/**
 * The AIR panel — everything about one request, and the next action for whoever opened it.
 *
 * The order of the sections is the order of the work: who it is for → what was asked and how it is
 * routed → what it costs → quote / invoice → shipments → notes. Each action is shown only to the
 * tier the resolver allows (UX; the server refuses either way).
 */
export function IntlAllRequestDrawer({ request, onClose }: { request: IntlAllRequest | null; onClose: () => void }) {
  const canFile = useCanFileAir();
  const canFulfil = useCanFulfilAir();
  const isRoot = useIsRootAir();
  const eligibility = useIntlAllEligibleModels();
  const [routing, setRouting] = useState(false);
  const [editingAddress, setEditingAddress] = useState(false);
  const [confirmUnroute, setConfirmUnroute] = useState(false);
  const [unroute, unrouteState] = useUnrouteIntlAllRequest();
  const [billing, setBilling] = useState<AirBillingMode | null>(null);
  const [noteText, setNoteText] = useState('');
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const routed = Boolean(request?.routedAt);
  const { data: pricingData, error: pricingError, loading: pricingLoading } = useIntlAllRequestPricing(
    request && routed && !request.stripeInvoiceId ? request.id : null,
  );
  const pricing = pricingData?.intlAllRequestPricing ?? null;
  const { data: notesData } = useIntlAllRequestNotes(request?.id ?? null);
  const [addNote, noteState] = useAddIntlAllRequestNote();
  const [setFreightOverride] = useSetIntlAllFreightOverride();
  const [createQuote, quoteState] = useCreateIntlAllQuote();
  const [cancelQuote, cancelState] = useCancelIntlAllQuote();
  const [refreshInvoice, refreshState] = useRefreshIntlAllInvoice();

  const swallow = () => {
    // Surfaced from the mutation's `error`.
  };

  const download = async () => {
    if (!request) return;
    setPdfError(null);
    setDownloading(true);
    try {
      await downloadAirQuotePdf(request.id);
    } catch (e) {
      setPdfError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  };

  const submitNote = async () => {
    if (!request || noteText.trim().length < 3) return;
    try {
      await addNote({ variables: { requestId: request.id, text: noteText.trim() } });
      setNoteText('');
    } catch {
      // Surfaced from noteState.error.
    }
  };

  const frozen = request ? routingFrozenReason(request) : null;
  // Fetch the route dialog's stock with the drawer (Paul, 2026-10-29), so the numbers are already
  // there the moment Route opens. Same query and variables as RouteDialog, so it reads this cache.
  const prefetchStock = Boolean(request) && canFulfil && !frozen;
  useIntlAllInventory(request?.id, { skip: !prefetchStock });
  useIntlAllMcfStock(request?.id ?? null, { skip: !prefetchStock || !isMcfServable(request?.shipToCountry ?? '') });
  const liveQuote = request ? hasLiveQuote(request) : false;
  const billingError = quoteState.error ?? cancelState.error ?? refreshState.error ?? null;

  return (
    <InShellPanel
      open={Boolean(request)}
      onOpenChange={(next) => !next && onClose()}
      side="right"
      data-testid="air-drawer"
      closeLabel="Close"
    >
      <div className="flex h-full min-h-0 flex-col">
        {request ? (
          <>
            <InShellPanelHeader className="shrink-0 pr-8">
              <InShellPanelTitle className="text-lg" data-testid="air-drawer-title">
                <span className="font-mono">{request.requestNo}</span>
                {request.company ? <span className="text-muted-foreground">{` · ${request.company}`}</span> : null}
              </InShellPanelTitle>
              <InShellPanelDescription>
                {request.contactEmail}
                <CopyButton value={request.contactEmail} label="Contact email" testId="air-drawer-copy-email" />
              </InShellPanelDescription>
            </InShellPanelHeader>

            <div className="flex shrink-0 flex-wrap items-center gap-2 px-6 pb-2">
              <Badge variant="secondary" data-testid="air-drawer-stage">
                {INTL_ALL_STAGE_LABELS[request.stage] ?? request.stage}
              </Badge>
              {request.invoiceStatus ? <IntlInvoiceStatusBadge status={request.invoiceStatus} /> : null}
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-6">
              <Section
                title="Customer"
                action={
                  // Root, support or fulfilment — until the request is routed (Paul, 2026-10-28).
                  (canFile || canFulfil) && !routed ? (
                    <Button size="sm" variant="outline" onClick={() => setEditingAddress(true)} data-testid="air-address-edit">
                      {'Edit shipping address'}
                    </Button>
                  ) : null
                }
              >
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <Field label="Stripe customer" value={request.stripeCustomerId} />
                  <Field label="Contact" value={request.contactName} />
                  <Field label="VAT / tax ID" value={request.vatNumber} />
                  <Field label="Currency" value={request.currency.toUpperCase()} />
                  <Field
                    wide
                    label="Ship to"
                    value={[
                      request.shipToName,
                      request.shipToLine1,
                      request.shipToLine2,
                      [request.shipToCity, request.shipToRegion, request.shipToPostalCode].filter(Boolean).join(' '),
                      countryName(request.shipToCountry),
                      request.shipToPhone,
                    ]
                      .filter(Boolean)
                      .join('\n')}
                  />
                </dl>
                {!request.addressComplete ? (
                  <p className="mt-2 text-xs text-destructive" data-testid="air-address-missing">
                    {`Address incomplete: ${request.addressMissing.join(', ')}.`}
                  </p>
                ) : null}
              </Section>

              <Section
                title="Devices and routing"
                action={
                  canFulfil && !frozen ? (
                    <div className="flex gap-2">
                      {/* Remove routing (Paul, 2026-10-28): back to filed, so the shipping address
                          can be corrected — only while nothing has been ordered on it. */}
                      {routed && !request.shipments.some((s) => s.ordered || s.shopifyOrderedAt) ? (
                        <Button size="sm" variant="outline" onClick={() => setConfirmUnroute(true)} data-testid="air-unroute-open">
                          {'Remove routing'}
                        </Button>
                      ) : null}
                      <Button size="sm" variant="outline" onClick={() => setRouting(true)} data-testid="air-route-open">
                        {routed ? 'Re-route' : 'Route'}
                      </Button>
                    </div>
                  ) : null
                }
              >
                {confirmUnroute ? (
                  <div className="mb-3 space-y-2 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" data-testid="air-unroute-confirm">
                    <p>{'Remove the routing? You can then edit the address and route it again.'}</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={unrouteState.loading}
                        onClick={() =>
                          unroute({ variables: { requestId: request.id } })
                            .then(() => setConfirmUnroute(false))
                            .catch(swallow)
                        }
                        data-testid="air-unroute-yes"
                      >
                        {unrouteState.loading ? 'Removing…' : 'Remove routing'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmUnroute(false)}>
                        {'Cancel'}
                      </Button>
                    </div>
                    {unrouteState.error ? <p className="text-xs text-destructive">{unrouteState.error.message}</p> : null}
                  </div>
                ) : null}
                <p className="text-sm" data-testid="air-drawer-lines">
                  {linesSummary(request.lines, INTL_DEVICE_LABELS)}
                </p>
                {routed ? (
                  <ul className="mt-2 space-y-0.5 text-sm" data-testid="air-drawer-allocations">
                    {request.allocations.map((a) => (
                      <li key={`${a.deviceModel}-${a.route}`}>
                        {`${a.quantity} × ${INTL_DEVICE_LABELS[a.deviceModel] ?? a.deviceModel} → ${INTL_ROUTE_LABELS[a.route]}`}
                      </li>
                    ))}
                    <li className="text-xs text-muted-foreground">{`Routed by ${request.routedBy ?? '—'}, ${fmt(request.routedAt)}`}</li>
                  </ul>
                ) : (
                  <p className="mt-2 text-xs text-muted-foreground">
                    {canFulfil ? 'Not routed yet — route it to price it.' : 'Waiting for fulfilment to route it.'}
                  </p>
                )}
                {frozen && canFulfil && !request.stripeInvoiceId ? <p className="mt-2 text-xs text-muted-foreground">{frozen}</p> : null}
              </Section>

              {routed && !request.stripeInvoiceId ? (
                <Section title="Price">
                  {pricing ? (
                    <PricingCard
                      pricing={pricing}
                      // Root + fulfilment, and only while nothing has been quoted or invoiced from it.
                      onSetFreight={
                        canFulfil && !frozen
                          ? (route, amountMinor) =>
                              setFreightOverride({ variables: { input: { requestId: request.id, route, amountMinor } } })
                          : undefined
                      }
                    />
                  ) : null}
                  {!pricing && pricingLoading ? (
                    <p className="animate-pulse text-sm text-muted-foreground" data-testid="air-pricing-loading">
                      {'Calculating price…'}
                    </p>
                  ) : null}
                  {pricingError ? (
                    <p className="text-sm text-destructive" data-testid="air-pricing-error">
                      {pricingError.message}
                    </p>
                  ) : null}
                </Section>
              ) : null}

              {/* Once invoiced, this section is only the quote's story — gone if there never was one. */}
              {routed && !(request.stripeInvoiceId && !request.stripeQuoteId) ? (
                <Section title={request.stripeInvoiceId ? 'Quote' : 'Quote and invoice'}>
                  <div className="space-y-2 text-sm">
                    {request.stripeQuoteId ? (
                      <p data-testid="air-quote-line">
                        {`Quote ${request.quoteNumber ?? request.stripeQuoteId} · ${request.quoteStatus ?? '—'}`}
                        {request.quoteTotal != null
                          ? ` · ${formatMinor(request.quoteTotal, request.quoteCurrency ?? request.currency)}`
                          : ''}
                        {request.quoteExpired ? ' · expired' : request.quoteExpiresAt ? ` · expires ${fmt(request.quoteExpiresAt)}` : ''}
                      </p>
                    ) : null}
                    {request.quoteError ? <p className="text-destructive">{request.quoteError}</p> : null}

                    <div className="flex flex-wrap gap-2">
                      {canFile && !request.stripeInvoiceId && !liveQuote ? (
                        <>
                          <Button
                            size="sm"
                            onClick={() => createQuote({ variables: { input: { requestId: request.id } } }).catch(swallow)}
                            disabled={quoteState.loading}
                            data-testid="air-quote-create"
                          >
                            {quoteState.loading ? 'Quoting…' : 'Create quote'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setBilling('invoice')} data-testid="air-invoice-open">
                            {'Raise invoice'}
                          </Button>
                        </>
                      ) : null}
                      {canFile && liveQuote && !request.stripeInvoiceId ? (
                        <>
                          <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="air-quote-pdf">
                            <Download className="mr-2 h-4 w-4" />
                            {'Quote PDF'}
                          </Button>
                          {!request.quoteExpired ? (
                            <Button size="sm" onClick={() => setBilling('convert')} data-testid="air-quote-convert">
                              {'Convert to invoice'}
                            </Button>
                          ) : null}
                        </>
                      ) : null}
                      {isRoot && liveQuote && !request.stripeInvoiceId ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => cancelQuote({ variables: { requestId: request.id } }).catch(swallow)}
                          disabled={cancelState.loading}
                          data-testid="air-quote-cancel"
                        >
                          {'Cancel quote'}
                        </Button>
                      ) : null}
                    </div>
                    {pdfError ? <p className="text-destructive">{pdfError}</p> : null}
                    {billingError ? (
                      <p className="text-destructive" data-testid="air-billing-action-error">
                        {billingError.message}
                      </p>
                    ) : null}
                  </div>
                </Section>
              ) : null}

              {request.stripeInvoiceId ? (
                <Section title="Invoice">
                  <InvoiceSection
                    request={request}
                    onRefresh={() => refreshInvoice({ variables: { requestId: request.id } }).catch(swallow)}
                    refreshing={refreshState.loading}
                  />
                </Section>
              ) : null}

              {routed ? (
                <Section title="Shipments">
                  <div className="space-y-3">
                    <FulfilApproval request={request} />
                    <ShipmentsPanel request={request} pricing={pricing} />
                  </div>
                </Section>
              ) : null}

              <Section title="Notes">
                <ul className="space-y-2 text-sm" data-testid="air-notes">
                  {(notesData?.intlAllRequestNotes ?? []).map((n) => (
                    <li key={n.id}>
                      <span className="text-xs text-muted-foreground">{`${n.staffEmail} · ${fmt(n.createdAt)}`}</span>
                      <p className="whitespace-pre-wrap">{n.text}</p>
                    </li>
                  ))}
                  {!(notesData?.intlAllRequestNotes ?? []).length ? (
                    <li className="text-xs text-muted-foreground">{'No notes.'}</li>
                  ) : null}
                </ul>
                {isRoot ? (
                  <div className="mt-3 flex gap-2">
                    <Input
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitNote();
                      }}
                      placeholder="Add a note"
                      data-testid="air-note-input"
                    />
                    <Button onClick={submitNote} disabled={noteState.loading || noteText.trim().length < 3} data-testid="air-note-add">
                      {'Add'}
                    </Button>
                  </div>
                ) : null}
                {noteState.error ? <p className="mt-1 text-sm text-destructive">{noteState.error.message}</p> : null}
              </Section>
            </div>

            <AddressDialog request={request} open={editingAddress} onOpenChange={setEditingAddress} />
            {routing ? (
              <RouteDialog
                request={request}
                eligibility={eligibility}
                pricing={pricing}
                open
                onOpenChange={(o) => {
                  if (!o) setRouting(false);
                }}
              />
            ) : null}
            {billing ? (
              <AirBillingDialog
                request={request}
                mode={billing}
                open
                onOpenChange={(o) => {
                  if (!o) setBilling(null);
                }}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </InShellPanel>
  );
}
